import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const RAPIDAPI_KEY = '4c66b9d960msh02b8d0e87b75da6p1592ebjsnb75d982894ba';

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' }
];

const LanguageContext = createContext();

// Translation cache to avoid repeated API calls
const translationCache = {};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translations, setTranslations] = useState({});

  // Detect browser language on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang) {
      setCurrentLanguage(savedLang);
    } else {
      const browserLang = navigator.language?.split('-')[0] || 'en';
      const supported = SUPPORTED_LANGUAGES.find(l => l.code === browserLang);
      if (supported) {
        setCurrentLanguage(browserLang);
      }
    }
  }, []);

  // Load cached translations from localStorage
  useEffect(() => {
    try {
      const cached = localStorage.getItem('translationCache');
      if (cached) {
        Object.assign(translationCache, JSON.parse(cached));
      }
    } catch (e) {
      console.error('Failed to load translation cache:', e);
    }
  }, []);

  const changeLanguage = useCallback((langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('preferredLanguage', langCode);
  }, []);

  const translateText = useCallback(async (text, targetLang) => {
    if (!text || targetLang === 'en') return text;
    
    const cacheKey = `${targetLang}:${text}`;
    
    // Check cache first
    if (translationCache[cacheKey]) {
      return translationCache[cacheKey];
    }

    try {
      const response = await fetch(
        `https://free-google-translator.p.rapidapi.com/external-api/free-google-translator?from=en&to=${targetLang}&query=${encodeURIComponent(text)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-rapidapi-host': 'free-google-translator.p.rapidapi.com',
            'x-rapidapi-key': RAPIDAPI_KEY
          },
          body: JSON.stringify({ translate: 'rapidapi' })
        }
      );

      const data = await response.json();
      if (data.translation) {
        translationCache[cacheKey] = data.translation;
        // Save to localStorage periodically
        try {
          localStorage.setItem('translationCache', JSON.stringify(translationCache));
        } catch (e) {
          // localStorage might be full
        }
        return data.translation;
      }
      return text;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  }, []);

  const value = {
    currentLanguage,
    changeLanguage,
    translateText,
    isTranslating,
    setIsTranslating,
    translations,
    setTranslations,
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { SUPPORTED_LANGUAGES };
export default LanguageContext;
